import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface AnimatedAutoBidInputProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  animateTo?: number | null;
  onAnimationComplete?: () => void;
}

export const AnimatedAutoBidInput = ({
  value,
  onChange,
  disabled,
  placeholder,
  className,
  animateTo = null,
  onAnimationComplete,
}: AnimatedAutoBidInputProps) => {
  const [display, setDisplay] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isFocused && animateTo == null) {
      setDisplay(value);
    }
  }, [value, isFocused, animateTo]);

  useEffect(() => {
    if (animateTo == null || isFocused) return;

    const start = value;
    const target = animateTo;
    if (start === target) {
      onChange(target);
      onAnimationComplete?.();
      return;
    }

    setIsAnimating(true);
    const t0 = performance.now();
    const duration = 450;

    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (p < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
        setIsAnimating(false);
        onChange(target);
        onAnimationComplete?.();
      }
    };

    animRef.current = requestAnimationFrame(tick);
    return () => {
      if (animRef.current != null) cancelAnimationFrame(animRef.current);
    };
  }, [animateTo, isFocused, onAnimationComplete, onChange, value]);

  const shown = isFocused ? value : isAnimating || animateTo != null ? display : value;

  return (
    <Input
      type="text"
      inputMode="numeric"
      value={shown > 0 ? String(shown) : ""}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, "");
        onChange(digits ? Number(digits) : 0);
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      disabled={disabled || isAnimating}
      placeholder={placeholder}
      className={cn(
        "h-10 text-base flex-1 tabular-nums transition-colors duration-300",
        isAnimating && "text-accent ring-1 ring-accent/50",
        className,
      )}
    />
  );
};
